import { useEffect, useRef, useState } from 'react';
import { usePomodoro, fmt, type Phase } from '../lib/pomodoro';
import { Modal } from './ui';

const PHASE_LABEL: Record<Phase, string> = { focus: 'Focus', short: 'Short break', long: 'Long break' };
const PHASE_VAR: Record<Phase, string> = { focus: 'var(--brand)', short: 'var(--c-amber)', long: 'var(--c-plum)' };

// ---------------------------------------------------------------------------
// Floating widget — mounted in <Layout>, so it survives every navigation and
// can be dragged anywhere on screen. Collapses to a pill.
// ---------------------------------------------------------------------------
export function PomodoroWidget() {
  const p = usePomodoro();
  const dragging = useRef(false);
  const grab = useRef({ dx: 0, dy: 0 });

  const show = (p.state.running || p.minimized) && !p.panelOpen;
  if (!show) return null;

  const placed = p.pos.x > 0 || p.pos.y > 0;
  const style: React.CSSProperties = placed
    ? { left: p.pos.x, top: p.pos.y, right: 'auto', bottom: 'auto' }
    : { right: 20, bottom: 20 };

  const onDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('[data-nodrag]')) return;
    dragging.current = true;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    grab.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const el = e.currentTarget as HTMLElement;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const x = Math.min(window.innerWidth - w - 8, Math.max(8, e.clientX - grab.current.dx));
    const y = Math.min(window.innerHeight - h - 8, Math.max(8, e.clientY - grab.current.dy));
    p.setPos({ x, y });
  };
  const onUp = (e: React.PointerEvent) => {
    dragging.current = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  if (p.minimized) {
    return (
      <div className="pomo pomo-pill" style={style} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} role="timer" aria-label={`Pomodoro ${PHASE_LABEL[p.state.phase]} ${fmt(p.remainingMs)}`}>
        <span className="pomo-pill-dot" style={{ background: PHASE_VAR[p.state.phase], animation: p.state.running ? 'pomoPulse 1.6s ease-in-out infinite' : 'none' }} aria-hidden />
        <span className="pomo-pill-time">{fmt(p.remainingMs)}</span>
        <button className="pomo-pill-open" data-nodrag onClick={() => p.minimize(false)} title="Expand timer">⤢</button>
      </div>
    );
  }

  const progress = p.totalMs > 0 ? 1 - p.remainingMs / p.totalMs : 0;
  return (
    <div className="pomo pomo-card" style={style} role="group" aria-label="Pomodoro timer">
      <div className="pomo-head" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}>
        <span className="pomo-grip" aria-hidden>⣿</span>
        <span className="pomo-phase" style={{ color: PHASE_VAR[p.state.phase] }}>{PHASE_LABEL[p.state.phase]}</span>
        <button className="pomo-mini" data-nodrag onClick={() => p.minimize(true)} title="Minimize">–</button>
      </div>
      <div className="pomo-body" data-nodrag>
        <Ring ms={p.remainingMs} progress={progress} color={PHASE_VAR[p.state.phase]} />
        <div className="pomo-side">
          <div className="pomo-time">{fmt(p.remainingMs)}</div>
          <div className="pomo-ctrl">
            <button className="pomo-btn primary" onClick={p.toggle} aria-label={p.state.running ? 'Pause' : 'Start'}>
              {p.state.running ? '❚❚' : '▶'}
            </button>
            <button className="pomo-btn" onClick={p.reset} title="Reset" aria-label="Reset">↺</button>
            <button className="pomo-btn" onClick={p.skip} title="Skip" aria-label="Skip">⏭</button>
          </div>
          <button className="pomo-expand" onClick={p.openTool}>Open full ↗</button>
        </div>
      </div>
    </div>
  );
}

function Ring({ ms, progress, color }: { ms: number; progress: number; color: string }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  const dash = c * Math.max(0, Math.min(1, progress));
  return (
    <svg className="pomo-ring" viewBox="0 0 80 80" width="80" height="80" aria-hidden>
      <circle cx="40" cy="40" r={r} fill="none" stroke="var(--surface2)" strokeWidth="6" />
      <circle
        cx="40"
        cy="40"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={dash}
        transform="rotate(-90 40 40)"
        style={{ transition: 'stroke-dashoffset .4s linear' }}
      />
      <text x="40" y="46" textAnchor="middle" className="pomo-ring-num">{fmt(ms)}</text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Full tool — the modal you open from the Tools page.
// ---------------------------------------------------------------------------
export function PomodoroTool() {
  const p = usePomodoro();
  if (!p.panelOpen) return null;

  const progress = p.totalMs > 0 ? 1 - p.remainingMs / p.totalMs : 0;

  return (
    <Modal
      title="Pomodoro timer"
      onClose={() => { p.closeTool(); if (p.state.running) p.minimize(true); }}
      footer={
        <div className="pomo-foot">
          <span className="muted" style={{ fontSize: 13 }}>
            {p.sessionsToday} focus {p.sessionsToday === 1 ? 'session' : 'sessions'} logged today
          </span>
          <button className="btn btn-outline btn-sm" onClick={p.reset}>Reset</button>
          <button className="btn btn-primary btn-sm" onClick={p.toggle}>{p.state.running ? 'Pause' : 'Start'}</button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 56, fontWeight: 600, fontVariantNumeric: 'tabular-nums', letterSpacing: '-.02em', marginBottom: 8 }}>
            {fmt(p.remainingMs)}
          </div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 16, color: PHASE_VAR[p.state.phase], fontWeight: 600 }}>
            {PHASE_LABEL[p.state.phase]}
          </div>
          <div className="pomo-dots" style={{ justifyContent: 'center', marginBottom: 16 }}>
            {Array.from({ length: p.settings.longEvery }).map((_, i) => (
              <span key={i} className="pomo-dot" style={{ background: i < p.state.cycles % p.settings.longEvery ? PHASE_VAR.focus : 'var(--surface2)' }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={p.toggle}>{p.state.running ? '❚❚ Pause' : '▶ Start'}</button>
            <button className="btn btn-outline" onClick={p.skip} title="Skip to next phase">⏭ Skip</button>
          </div>
          <div className="bar" style={{ maxWidth: 280, margin: '18px auto 0' }} role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${Math.min(100, progress * 100)}%`, background: PHASE_VAR[p.state.phase] }} />
          </div>
        </div>

        <div style={{ flex: '1 1 240px', maxWidth: 300 }}>
          <div className="eyebrow" style={{ marginBottom: 12 }}>
            <span className="tick" aria-hidden />
            <span>Settings</span>
          </div>
          <NumField label="Focus (min)" value={p.settings.focusMin} min={1} max={180} onChange={v => p.setSettings({ focusMin: v })} />
          <NumField label="Short break (min)" value={p.settings.shortMin} min={1} max={60} onChange={v => p.setSettings({ shortMin: v })} />
          <NumField label="Long break (min)" value={p.settings.longMin} min={1} max={90} onChange={v => p.setSettings({ longMin: v })} />
          <NumField label="Long break every" value={p.settings.longEvery} min={2} max={8} onChange={v => p.setSettings({ longEvery: v })} />
          <label className="pomo-toggle">
            <input type="checkbox" checked={p.settings.autoStartBreaks} onChange={e => p.setSettings({ autoStartBreaks: e.target.checked })} />
            <span>Auto-start breaks</span>
          </label>
          <div className="pomo-dots" style={{ marginTop: 16 }}>
            {(['focus', 'short', 'long'] as Phase[]).map(ph => (
              <button key={ph} className={`pomo-chip${p.state.phase === ph ? ' on' : ''}`} onClick={() => p.setPhase(ph)}>
                {PHASE_LABEL[ph]}
              </button>
            ))}
          </div>
          <p className="muted" style={{ fontSize: 12.5, marginTop: 14, lineHeight: 1.5 }}>
            The timer keeps running when you leave this page — minimize it and it follows you around the
            site as a small draggable widget.
          </p>
        </div>
      </div>
    </Modal>
  );
}

function NumField({ label, value, min, max, onChange }: { label: string; value: number; min: number; max: number; onChange: (v: number) => void }) {
  const [focus, setFocus] = useState(false);
  const [text, setText] = useState(String(value));
  useEffect(() => {
    if (!focus) setText(String(value));
  }, [value, focus]);
  const commit = (raw: string) => {
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)));
    else setText(String(value));
  };
  return (
    <div className="pomo-num">
      <span>{label}</span>
      <input
        type="number"
        inputMode="numeric"
        value={focus ? text : String(value)}
        min={min}
        max={max}
        onFocus={() => setFocus(true)}
        onChange={e => setText(e.target.value)}
        onBlur={e => {
          setFocus(false);
          commit(e.target.value);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
    </div>
  );
}
